<script setup lang="ts">
import {
  ref,
  computed,
  type ComputedRef,
  toRefs,
  nextTick,
  type Ref,
} from 'vue';
import type { ModuleFolderMapping } from '@/models/RobotFinder';
import gettext from '@/i18n/gettext';
import type { ModuleIdNameMapping } from '@/models/Robot';
import { getModuleIdNameMapping } from '@/helpers/RobotHelper';

const { $gettext } = gettext;

const props = defineProps<{
  moduleFolderMapping: ModuleFolderMapping;
}>();

const emit = defineEmits<{
  (e: 'onModuleSelected', modules: ModuleIdNameMapping): void;
  (e: 'onModulesBulkSelected', modules: ModuleIdNameMapping[]): void;
  (e: 'onUnmatchedIds', ids: string[]): void;
}>();

const moduleSelector = ref();
const { moduleFolderMapping } = toRefs(props);

const currentModuleSelection: Ref<ModuleIdNameMapping | undefined> =
  ref(undefined);

const moduleIdNameMapping: ComputedRef<ModuleIdNameMapping[]> = computed(() => {
  return getModuleIdNameMapping(moduleFolderMapping.value);
});

function onModuleSelected() {
  nextTick(() => {
    // clear moduleSelector field
    (
      (moduleSelector.value.$el as HTMLElement).getElementsByClassName(
        'no-icon',
      )[0] as HTMLElement
    ).click();

    // request focus on moduleSelector again
    (
      (moduleSelector.value.$el as HTMLElement).getElementsByTagName(
        'input',
      )[0] as HTMLInputElement
    ).focus();
  });
  if (currentModuleSelection.value) {
    emit('onModuleSelected', currentModuleSelection.value);
  }
}

function findModuleById(id: string): ModuleIdNameMapping | undefined {
  const numericId = parseInt(id, 10);
  return moduleIdNameMapping.value.find(
    (m) => parseInt(m.id, 10) === numericId,
  );
}

function filterByNameOrId(
  itemTitle: string,
  queryText: string,
  item?: { raw: ModuleIdNameMapping },
) {
  const search = queryText.toLowerCase();
  return (
    itemTitle.toLowerCase().includes(search) ||
    (item?.raw.id ?? '').includes(search)
  );
}

function onPaste(event: ClipboardEvent) {
  const pastedText = event.clipboardData?.getData('text');
  if (!pastedText) {
    return;
  }

  const ids = pastedText
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /^\d+$/.test(s));

  if (ids.length < 2) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const modules: ModuleIdNameMapping[] = [];
  const unmatchedIds: string[] = [];
  for (const id of ids) {
    const mapping = findModuleById(id);
    if (mapping) {
      modules.push(mapping);
    } else {
      unmatchedIds.push(id);
    }
  }

  if (modules.length > 0) {
    emit('onModulesBulkSelected', modules);
  }

  if (unmatchedIds.length > 0) {
    emit('onUnmatchedIds', unmatchedIds);
  }
}
</script>
<template>
  <v-autocomplete
    ref="moduleSelector"
    v-model="currentModuleSelection"
    clearable
    clear-icon="no-icon"
    :label="$gettext('Select Modules')"
    :placeholder="$gettext('Search or paste comma-separated IDs')"
    :items="moduleIdNameMapping"
    :return-object="true"
    item-title="name"
    item-value="id"
    :no-data-text="$gettext('No modules available')"
    :custom-filter="filterByNameOrId"
    @update:modelValue="onModuleSelected"
    @paste="onPaste"
  >
    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" :subtitle="`ID: ${item.raw.id}`" />
    </template>
  </v-autocomplete>
</template>
